import { useState } from 'react';
import { ProfileIdentity } from '../components/ProfileIdentity';
import { DemoPro, readDemoPro } from '../components/DemoPro';
import { useNavigate } from 'react-router-dom';
import { parseNumericInput, validDailyCalorieTarget, type Goal, type User } from '@jungles/shared';
import { CalorieCalculator } from '../components/CalorieCalculator';
import { api } from '../lib/api';
import { useSession, useUser } from '../app/session';
import { useToast } from '../app/toast';
import { BackLink, Header, Screen, TabBar } from '../components/Chrome';
import { Dialog } from '../components/Sheet';

/** P2–P3 · Профиль (SPEC §5). */

import { GoalPicker } from '../components/GoalPicker';

export function ProfileScreen() {
  const user = useUser();
  const { updateUser, logout } = useSession();
  const navigate = useNavigate();
  const toast = useToast();


  const [height, setHeight] = useState(String(user.heightCm ?? ''));
  const [weight, setWeight] = useState(String(user.weightKg ?? '').replace('.', ','));
  const [pro, setPro] = useState(() => readDemoPro(user.id));
  const [paywall, setPaywall] = useState(false);
  const [goalDays, setGoalDays] = useState(String(user.weeklyWorkoutGoal));
  const [goal, setGoal] = useState<Goal>(user.goal);
  const [calories, setCalories] = useState(String(user.dailyCalorieTarget ?? ''));
  const [calculator, setCalculator] = useState(false);
  const [timezone, setTimezone] = useState(user.timezone);
  const [timezoneEdited, setTimezoneEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const save = async () => {
    const dailyCalorieTarget = calories.trim() ? parseNumericInput(calories, true) : null;
    if (dailyCalorieTarget !== null && !validDailyCalorieTarget(dailyCalorieTarget)) {
      toast('Введите целое число от 1 до 20 000 или очистите поле. Это пределы ввода, не рекомендация.'); return;
    }
    const weeklyWorkoutGoal = parseNumericInput(goalDays, true);
    if (!Number.isInteger(weeklyWorkoutGoal) || weeklyWorkoutGoal < 1 || weeklyWorkoutGoal > 14) {
      toast('Цель — от 1 до 14 тренировок в неделю');
      return;
    }
    const heightCm = height.trim() ? parseNumericInput(height, true) : null;
    const weightKg = weight.trim() ? parseNumericInput(weight) : null;
    if ((heightCm !== null && (!Number.isInteger(heightCm) || heightCm < 100 || heightCm > 250)) || (weightKg !== null && (!Number.isFinite(weightKg) || weightKg < 30 || weightKg > 300))) {
      toast('Проверьте имя, рост (100–250 см) и вес (30–300 кг)'); return;
    }
    setBusy(true);
    try {
      const next = await api.patch<User>('/api/me', {

        heightCm, weightKg,
        dailyCalorieTarget,
        weeklyWorkoutGoal,
        goal,
        ...(timezoneEdited ? {timezone} : {}),
      });
      updateUser(next,true);
      toast('Настройки сохранены');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const toggleTheme = async () => {
    const theme = user.theme === 'dark' ? 'light' : 'dark';
    try {
      const next = await api.patch<User>('/api/me', { theme });
      updateUser(next,true);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось сменить тему');
    }
  };

  const restartOnboarding = async () => {
    try {
      const next = await api.post<User>('/api/me/onboarding/reset');
      updateUser(next,true);
      navigate('/onboarding');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось открыть знакомство');
    }
  };

  return (
    <Screen tabs>
      <Header showAvatar={false} />
      <BackLink to="/" label="Главная" />
      <ProfileIdentity key={user.id} user={user}/>
      <section className="profile-pro">
        <img src="/assets/ui/points-diamond-bicep.webp" alt="" />
        <b>Jungles Pro · демо</b><h2>{pro ? 'Демо Pro включено' : 'Считайте КБЖУ по одному фото'}</h2>
        <p>Фото блюда → примерные калории и БЖУ</p>
        <button className="b wide" onClick={() => setPaywall(true)}>{pro ? 'О деморежиме' : 'Попробовать Pro'}</button>
        <small>Без оплаты и автопродления</small>
      </section>
      <section className="card profile-parameters">
        <h2>Параметры и цели</h2>
        <div className="profile-measures">
          <label className="fl">Рост, см<input className="f" inputMode="numeric" value={height} onChange={e => setHeight(e.target.value)} /></label>
          <label className="fl">Вес, кг<input className="f" inputMode="decimal" value={weight} onChange={e => setWeight(e.target.value)} /></label>
        </div>
        <div className="profile-weekly"><label htmlFor="weekly-goal">Тренировок в неделю</label>
          <button className="round" aria-label="Меньше тренировок" disabled={Number(goalDays) <= 1} onClick={() => setGoalDays(String(Math.max(1, Number(goalDays) - 1)))}>−</button>
          <input id="weekly-goal" className="f" inputMode="numeric" value={goalDays} onChange={e => setGoalDays(e.target.value)} />
          <button className="round" aria-label="Больше тренировок" disabled={Number(goalDays) >= 14} onClick={() => setGoalDays(String(Math.min(14, Number(goalDays) + 1)))}>+</button>
        </div>
        <GoalPicker value={goal} onChange={setGoal} />
        <div className="profile-calories">
          <label className="fl">Ориентир, ккал/день<input className="f" inputMode="numeric" value={calories} onChange={e => setCalories(e.target.value)} /></label>
          <p className="nt">Ваш дневной ориентир. Пустое поле уберёт его при сохранении.</p>
          <button className="b wide" type="button" onClick={() => setCalculator(true)}>Рассчитать ориентир</button>
        </div>

        <label className="fl">
          Часовой пояс
          <input
            className="f"
            value={timezoneEdited ? timezone : user.timezone}
            onChange={(event) => {setTimezone(event.target.value);setTimezoneEdited(true);}}
            placeholder="Europe/Moscow"
          />
        </label>
        <p className="nt">
          По поясу считаются границы дня и недели: очки, серии и лиги. Меняйте, только если
          переехали.
        </p>

        <button className="b p wide" type="button" onClick={() => void save()} disabled={busy}>
          Сохранить настройки
        </button>
      </section>

      <section className="card">
        <h2>Под вашим контролем</h2>
        <div className="stack">
          <button className="b" type="button" onClick={() => navigate('/profile/notifications')}>Уведомления</button>
          <button className="b" type="button" onClick={() => navigate('/profile/privacy')}>
            Приватность и участие
          </button>
          <button className="b" type="button" onClick={() => void toggleTheme()}>
            {user.theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
          </button>
          <button className="b" type="button" onClick={() => void restartOnboarding()}>
            Пройти знакомство ещё раз
          </button>
          <button className="b d" type="button" onClick={() => setConfirmLogout(true)}>
            Выйти
          </button>
        </div>
        <p className="nt">
          Тема хранится в профиле и применяется на всех устройствах. Записи привязаны к аккаунту
          Telegram.
        </p>
      </section>

      <p className="nt">
        Данные о продуктах: <a href="https://world.openfoodfacts.org" target="_blank" rel="noreferrer">Open Food Facts</a>
        {' · '}<a href="https://opendatacommons.org/licenses/odbl/1-0/" target="_blank" rel="noreferrer">ODbL</a>.
      </p>

      <TabBar />
      {calculator && <CalorieCalculator heightCm={user.heightCm} weightKg={user.weightKg} goal={goal} onClose={() => setCalculator(false)} onApply={value => { setCalories(String(value)); setCalculator(false); }} />}
      {paywall && <DemoPro onClose={() => setPaywall(false)} onEnabled={() => { setPro(true); setPaywall(false); }} />}

      {confirmLogout ? (
        <Dialog
          title="Выйти из аккаунта?"
          confirmLabel="Выйти"
          danger
          onCancel={() => setConfirmLogout(false)}
          onConfirm={() => {
            logout();
            navigate('/', { replace: true });
          }}
        >
          <p>
            Дневник будет скрыт на этом устройстве. Данные останутся на сервере — вход через
            Telegram вернёт их.
          </p>
        </Dialog>
      ) : null}
    </Screen>
  );
}
